/**
 * Public guest view for a bill-split session.
 *
 * Mounted at /split/:code via App.js — no auth required. The share code
 * IS the secret. Users land here from a host's iMessage / WhatsApp share
 * link (or a Universal Link forwarded by the native app shell).
 *
 * Flow:
 *   1. Prompt for first name (if not already stored for this code on this device)
 *   2. Join via POST /api/splits/:code/join
 *      - If 402 → "Free splits cap at 4 — ask the host to upgrade or join later" toast
 *   3. Subscribe to /splits/:code/stream — live state
 *   4. Tap items to toggle claim. Optimistic UI; server is authoritative on conflict.
 *   5. Show running total = subtotal + proportional tax + proportional tip
 *   6. Render payment deep-link buttons + "I paid" / "Cancel paid" toggle.
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  getSplit,
  joinSplit,
  claimItem,
  selfMarkPaid,
  useSplitStream,
  formatMoney,
  buildPaymentLinks,
  getStoredParticipantId,
  storeParticipantId,
  getStoredParticipantName,
  statusLabel,
} from "../../lib/billSplit";

export default function BillSplitGuestView() {
  const { code } = useParams();
  const upperCode = (code || "").toUpperCase();

  const [nameInput, setNameInput] = useState("");
  const [participantId, setParticipantId] = useState(() => getStoredParticipantId(upperCode));
  const [joining, setJoining] = useState(false);
  const [bootstrap, setBootstrap] = useState({ loading: true, notFound: false, expired: false });

  // Live stream — drives the rendered state.
  const { split, setSplit, connected } = useSplitStream(participantId ? upperCode : null);

  // Bootstrap so guests see something while they're typing their name.
  useEffect(() => {
    let cancelled = false;
    getSplit(upperCode)
      .then((data) => {
        if (cancelled) return;
        setSplit(data);
        setBootstrap({ loading: false, notFound: false, expired: false });
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        setBootstrap({
          loading: false,
          notFound: status === 404,
          expired: status === 410,
        });
      });
    return () => { cancelled = true; };
  }, [upperCode, setSplit]);

  const handleJoin = useCallback(async () => {
    const name = nameInput.trim();
    if (!name) { toast.error("Enter your first name to join."); return; }
    setJoining(true);
    try {
      const res = await joinSplit(upperCode, name);
      setParticipantId(res.participant_id);
      storeParticipantId(upperCode, res.participant_id, name);
      setSplit(res.split);
      toast.success(`Welcome, ${name}!`);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 402) {
        toast.error("This split is at its 4-person free-tier cap. Ask the host to upgrade.");
      } else if (status === 410) {
        toast.error("This split session has expired.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Couldn't join split.");
      }
    } finally {
      setJoining(false);
    }
  }, [nameInput, upperCode, setSplit]);

  const participant = useMemo(
    () => split?.participants?.find((p) => p.id === participantId) || null,
    [split, participantId]
  );

  const handleClaimToggle = useCallback(
    async (item) => {
      if (!participantId) return;
      const mine = item.claimed_by === participantId;
      const claimedByOther = item.claimed_by && item.claimed_by !== participantId;
      if (claimedByOther) {
        const claimerName =
          split.participants.find((p) => p.id === item.claimed_by)?.name || "Someone";
        toast.error(`${claimerName} already claimed ${item.name}.`);
        return;
      }
      // Optimistic update
      setSplit((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((it) =>
            it.id === item.id
              ? { ...it, claimed_by: mine ? null : participantId }
              : it
          ),
        };
      });
      try {
        const next = await claimItem(upperCode, {
          itemId: item.id,
          participantId,
          unclaim: mine,
        });
        setSplit(next);
      } catch (err) {
        toast.error("Couldn't update — refresh and try again.");
        // Resync from server
        getSplit(upperCode).then(setSplit).catch(() => {});
      }
    },
    [participantId, upperCode, split, setSplit]
  );

  const handlePaymentTap = useCallback(
    async (link) => {
      // Open the payment deep link in a new tab/scheme handler.
      window.location.href = link.url;
    },
    []
  );

  const handleMarkPaid = useCallback(
    async (unmark = false) => {
      if (!participantId) return;
      try {
        const next = await selfMarkPaid(upperCode, participantId, unmark);
        setSplit(next);
        toast.success(unmark ? "Marked unpaid." : "Thanks — host will confirm.");
      } catch (err) {
        toast.error("Couldn't update — try again.");
      }
    },
    [participantId, upperCode, setSplit]
  );

  /* -------- Render -------- */

  if (bootstrap.loading) {
    return <Centered>Loading split…</Centered>;
  }
  if (bootstrap.notFound) {
    return (
      <Centered>
        <h1 className="font-jakarta font-extrabold text-2xl text-navy mb-2">
          Split not found
        </h1>
        <p className="font-jakarta text-navy-soft text-sm">
          The share link may be wrong or the session may have closed.
        </p>
      </Centered>
    );
  }
  if (bootstrap.expired) {
    return (
      <Centered>
        <h1 className="font-jakarta font-extrabold text-2xl text-navy mb-2">
          This split has expired
        </h1>
        <p className="font-jakarta text-navy-soft text-sm">
          Splits stay live for 72 hours. Ask the host to start a new one.
        </p>
      </Centered>
    );
  }
  if (!split) return <Centered>Loading…</Centered>;

  // Pre-join — name entry
  if (!participantId) {
    const existingName = getStoredParticipantName(upperCode);
    if (existingName && !nameInput) setNameInput(existingName);
    return (
      <PageShell split={split}>
        <div className="bg-white rounded-2xl border border-navy/5 p-5 space-y-4" data-testid="split-join-card">
          <header>
            <h2 className="font-jakarta font-extrabold text-xl text-navy">
              Join {split.host_name || "the host"}&apos;s split
            </h2>
            <p className="font-jakarta text-sm text-navy-soft mt-1">
              Enter your first name to claim what you ordered.
            </p>
          </header>
          <input
            data-testid="split-name-input"
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            placeholder="Your first name"
            maxLength={40}
            className="w-full bg-bone/40 border border-navy/10 rounded-xl px-4 py-3 font-jakarta font-bold text-navy outline-none focus:border-orange"
            onKeyDown={(e) => { if (e.key === "Enter") handleJoin(); }}
          />
          <button
            type="button"
            data-testid="split-join-btn"
            onClick={handleJoin}
            disabled={joining || !nameInput.trim()}
            className="w-full bg-orange text-white rounded-full py-3.5 font-jakarta font-extrabold disabled:opacity-50 active:scale-[0.98] transition-transform"
          >
            {joining ? "Joining…" : "Join split"}
          </button>
          <p className="font-jakarta text-xs text-navy-soft text-center">
            No account needed · Your name is visible to others in the split.
          </p>
        </div>
      </PageShell>
    );
  }

  // Joined — main item list view
  return (
    <PageShell split={split}>
      <LiveBadge connected={connected} />
      <ItemList
        split={split}
        participantId={participantId}
        onToggleClaim={handleClaimToggle}
      />
      <YourTotal
        split={split}
        participant={participant}
      />
      <PaymentSection
        split={split}
        participant={participant}
        onPaymentTap={handlePaymentTap}
        onMarkPaid={handleMarkPaid}
      />
      <Participants split={split} participantId={participantId} />
    </PageShell>
  );
}

/* ---------------------------- Building blocks ---------------------------- */

function PageShell({ split, children }) {
  return (
    <div className="min-h-screen bg-bone/40 pb-32">
      <header className="px-5 pt-6 pb-3">
        <p className="font-jakarta text-xs uppercase tracking-wider text-navy-soft">
          TipLingo · Split the bill
        </p>
        <h1 className="font-jakarta font-extrabold text-2xl text-navy mt-1">
          Code {(split?.code || "").toUpperCase()}
        </h1>
        <p className="font-jakarta text-sm text-navy-soft mt-0.5">
          {statusLabel(split?.status)}
        </p>
      </header>
      <main className="px-5 space-y-4">{children}</main>
    </div>
  );
}

function Centered({ children }) {
  return (
    <div className="min-h-screen bg-bone/40 flex items-center justify-center px-6 text-center">
      <div>{children}</div>
    </div>
  );
}

function LiveBadge({ connected }) {
  return (
    <div
      className="flex items-center gap-2 text-xs font-jakarta text-navy-soft"
      data-testid="split-live-badge"
    >
      <span
        className={`w-2 h-2 rounded-full ${connected ? "bg-emerald-500" : "bg-amber-500"}`}
      />
      {connected ? "Live — updates in real time" : "Reconnecting…"}
    </div>
  );
}

function ItemList({ split, participantId, onToggleClaim }) {
  return (
    <section
      className="bg-white rounded-2xl border border-navy/5 divide-y divide-navy/5 overflow-hidden"
      data-testid="split-items"
    >
      {split.items.map((it) => {
        const claimedByMe = it.claimed_by === participantId;
        const claimedByOther = it.claimed_by && !claimedByMe;
        const claimerName =
          claimedByOther
            ? split.participants.find((p) => p.id === it.claimed_by)?.name
            : null;
        return (
          <button
            key={it.id}
            type="button"
            data-testid={`split-item-${it.id}`}
            onClick={() => onToggleClaim(it)}
            disabled={!!claimedByOther}
            className={[
              "w-full text-left px-4 py-3 flex items-center justify-between gap-3 transition-colors",
              claimedByOther ? "opacity-50 cursor-not-allowed" : "active:bg-bone/60",
              claimedByMe ? "bg-orange/10" : "",
            ].join(" ")}
          >
            <div className="flex-1 min-w-0">
              <div className="font-jakarta font-bold text-navy text-base truncate">
                {it.name}
              </div>
              {(claimedByMe || claimedByOther) && (
                <div className="font-jakarta text-xs text-navy-soft mt-0.5">
                  {claimedByMe ? "You claimed this · tap to release" : `Claimed by ${claimerName}`}
                </div>
              )}
            </div>
            <div className="font-jakarta font-extrabold text-navy text-base flex-shrink-0">
              {formatMoney(it.price_cents, split.currency)}
            </div>
          </button>
        );
      })}
    </section>
  );
}

function YourTotal({ split, participant }) {
  if (!participant || !participant.totals) return null;
  const t = participant.totals;
  return (
    <section
      className="bg-navy text-white rounded-2xl p-5 space-y-2"
      data-testid="split-your-total"
    >
      <h2 className="font-jakarta font-extrabold text-base">Your share</h2>
      <Row label="Subtotal" value={formatMoney(t.subtotal_cents, split.currency)} />
      <Row label="Tax (proportional)" value={formatMoney(t.tax_cents, split.currency)} />
      <Row label="Tip (proportional)" value={formatMoney(t.tip_cents, split.currency)} />
      <div className="border-t border-white/15 mt-2 pt-2 flex justify-between font-extrabold">
        <span>You owe</span>
        <span data-testid="split-your-total-amount">
          {formatMoney(t.total_cents, split.currency)}
        </span>
      </div>
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between font-jakarta text-sm">
      <span className="text-white/70">{label}</span>
      <span>{value}</span>
    </div>
  );
}

function PaymentSection({ split, participant, onPaymentTap, onMarkPaid }) {
  if (!participant) return null;
  const paid = participant.paid_status === "marked_paid";
  const total = participant.totals?.total_cents || 0;
  const links = buildPaymentLinks(split, total);

  if (paid) {
    return (
      <section
        className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 space-y-3"
        data-testid="split-paid-section"
      >
        <div className="font-jakarta font-extrabold text-emerald-700 text-base">
          ✓ You&apos;re marked as paid
        </div>
        <p className="font-jakarta text-sm text-emerald-700">
          {participant.marked_paid_by === "host"
            ? "The host confirmed your payment."
            : "Host will confirm shortly."}
        </p>
        <button
          type="button"
          data-testid="split-unmark-paid-btn"
          onClick={() => onMarkPaid(true)}
          className="font-jakarta font-bold text-sm text-emerald-700 underline"
        >
          Not paid yet? Tap to undo.
        </button>
      </section>
    );
  }

  if (total === 0) {
    return (
      <section className="bg-white rounded-2xl border border-navy/5 p-5">
        <p className="font-jakarta text-sm text-navy-soft text-center">
          Claim items above to see your total + payment options.
        </p>
      </section>
    );
  }

  return (
    <section
      className="bg-white rounded-2xl border border-navy/5 p-5 space-y-3"
      data-testid="split-payment-section"
    >
      <h2 className="font-jakarta font-extrabold text-base text-navy">
        Pay {split.host_name || "the host"}
      </h2>
      <p className="font-jakarta text-xs text-navy-soft">
        Tap a button to open the payment app with the amount pre-filled. After paying,
        come back and tap &ldquo;I paid.&rdquo;
      </p>
      {links.length > 0 ? (
        <div className="space-y-2">
          {links.map((l) => (
            <button
              key={l.method}
              type="button"
              data-testid={`split-pay-${l.method}-btn`}
              onClick={() => onPaymentTap(l)}
              className="w-full bg-navy text-white rounded-full py-3 font-jakarta font-extrabold text-sm active:scale-[0.98] transition-transform"
            >
              {l.label}
            </button>
          ))}
        </div>
      ) : (
        <div className="text-xs text-navy-soft italic font-jakarta">
          Host hasn&apos;t set up payment links yet — pay them directly and tap &ldquo;I paid.&rdquo;
        </div>
      )}
      <button
        type="button"
        data-testid="split-mark-paid-btn"
        onClick={() => onMarkPaid(false)}
        className="w-full bg-orange/10 text-orange rounded-full py-3 font-jakarta font-extrabold text-sm border border-orange/30"
      >
        I paid {formatMoney(total, split.currency)}
      </button>
    </section>
  );
}

function Participants({ split, participantId }) {
  return (
    <section className="bg-white rounded-2xl border border-navy/5 p-5">
      <h3 className="font-jakarta font-extrabold text-xs uppercase tracking-wider text-navy-soft mb-3">
        In this split
      </h3>
      <ul className="space-y-2" data-testid="split-participants">
        {split.participants.map((p) => {
          const isMe = p.id === participantId;
          const total = p.totals?.total_cents || 0;
          return (
            <li
              key={p.id}
              className="flex items-center justify-between gap-3 font-jakarta text-sm"
            >
              <span className="font-bold text-navy">
                {p.name}{isMe && " (you)"}
              </span>
              <span className="text-navy-soft">
                {p.paid_status === "marked_paid" ? "✓ paid · " : ""}
                {formatMoney(total, split.currency)}
              </span>
            </li>
          );
        })}
        {split.participants.length === 0 && (
          <li className="font-jakarta text-sm text-navy-soft italic">
            Waiting for guests to join…
          </li>
        )}
      </ul>
    </section>
  );
}
